<template>
  <div
    class="deal-showcase"
    :class="{
      'deal-showcase-is-women': isWomen,
      'deal-showcase-is-sneaker-socks': isSneakerSocks,
      'deal-showcase-is-socks-regular': isSocksRegular,
      'deal-showcase-is-boys-originals': isBoysOriginals,
      'deal-showcase-is-men-originals': isMenOriginals,
      'deal-showcase-is-men-one': isMenOne,
      'deal-showcase-is-undershirts': isUndershirts }"
  >
    <div
      class="deal-showcase-image-container relative overflow-hidden"
    >
      <OImage v-bind="productImageOne" class="max-h-full" img-class="deal-showcase-image pointer-events-none h-full w-full" />
      <div v-if="pills.length > 0" class="absolute top-0 right-0 h-full p-4">
        <span
          v-for="pill in pills"
          :key="pill.text"
          class="o-body text-white font-bold whitespace-no-wrap px-3 py-1 rounded-4rem mr-2"
          :style="{ backgroundColor: pill.color }"
          v-text="pill.text"
        />
      </div>
    </div>
    <div class="p-4">
      <p class="o-heading-2 mb-4 font-24" v-text="$t(`MemberShipTypes.${deal.reference}.Name`)" />
      <div v-for="(usp, index) in usps" :key="`${usp.text}-${usp.icon}-${index}`" class="flex items-center gap-4 mb-2 text-neutral-600">
        <OSvg :svg="usp.icon" class="w-4 flex-shrink-0 fill-neutral-600" />
        <span class="o-body font-14" v-text="usp.text" />
      </div>
      <div class="my-4 flex justify-between items-center">
        <div>
          <span v-if="showMostPopular" class="ota-p16 font-semibold text-newBlue-400" v-text="$t('ExploreMemberships.MostPopular')" />
        </div>
        <div>
          <span v-if="hasDiscount" class="text-neutral-600 ota-p16 line-through mr-2" v-text="prices.originalPrice" />
          <span class="text-price font-semibold" v-text="hasDiscount ? prices.discountPrice : prices.originalPrice" />
        </div>
      </div>
      <NewButton color="yellow" class="w-full" icon-right="new-chevron-right" @click="onSelectDealClick(deal)">
        {{ $t('ExploreMemberships.SelectDeal') }}
      </NewButton>
    </div>
  </div>
</template>

<script>
import NewButton from '@/components/NewButton'
import OImage from '@/components/OImage'
import OSvg from '@/components/OSvg'
import { CountryCodes, Locales, Currencies } from '@/enums/general'
import { addLocalCurrency, getImagePaths } from '@/utils/general'
import { MembershipPrice, MembershipTypes } from '@/enums/member'
import { Orientations } from '@/enums/products'
import { CampaignStrategies } from '@/enums/campaigns'

export default {
  name: 'DealShowcase',
  components: {
    NewButton,
    OImage,
    OSvg
  },
  props: {
    deal: {
      type: Object,
      required: true,
      validator: value => ['id', 'name', 'reference', 'campaignEligible', 'campaignStrategy'].every(prop => value.hasOwnProperty(prop))
    },
    latestDesigns: {
      type: Object,
      required: true,
      validator: (value) => {
        if (typeof value !== 'object' || value === null || Array.isArray(value)) {
          return false
        }

        const keys = Object.keys(value)
        if (keys.length === 0) {
          return false
        }

        return keys.every((key) => {
          const child = value[key]
          return (
            typeof child === 'object' &&
            child !== null &&
            child.hasOwnProperty('primaryColor') &&
            child.hasOwnProperty('slug')
          )
        })
      }
    },
    country: {
      type: String,
      default: CountryCodes.netherlands
    },
    locale: {
      type: String,
      default: Locales.nl
    },
    hasDiscount: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    showMostPopular () {
      return [MembershipTypes.sneakerSocks, MembershipTypes.socksRegular].includes(this.deal.reference)
    },
    prices () {
      const countryCode = `${this.locale}-${this.country}`

      const originalPrice = MembershipPrice[this.deal.reference][this.country]
      const discountPrice = MembershipPrice[this.deal.reference].discount?.[this.country]

      return {
        originalPrice: addLocalCurrency(originalPrice, countryCode, Currencies[this.country]).replace(',00', '').replace('.00', ''),
        discountPrice: addLocalCurrency(discountPrice, countryCode, Currencies[this.country]).replace(',00', '').replace('.00', ''),
        originalPriceNumber: originalPrice,
        discountPriceNumber: discountPrice
      }
    },
    pills () {
      const pills = []

      if (this.deal.campaignEligible && this.deal.campaignStrategy === CampaignStrategies.extraOrder) {
        pills.push({
          text: this.getTranslation('Pills.FirstForFree'),
          color: 'rgba(99, 98, 236, 1)'
        })
      }

      if (this.hasDiscount) {
        pills.push({
          text: this.getTranslation('Pills.DiscountPercentage'),
          color: 'rgba(230, 70, 76, 1)'
        })
      }

      return pills
    },
    latestDesign () {
      let matchKey = Object.keys(this.latestDesigns).find(key => key === this.deal.reference.replace('-membership', ''))

      if (this.isWomen) {
        matchKey = Orientations.womenhipster
      }
      return matchKey ? this.latestDesigns[matchKey] : undefined
    },
    primaryColor () {
      return this.latestDesign?.primaryColor || '57,74,59'
    },
    latestDesignSlug () {
      return this.latestDesign?.slug || 'boriky'
    },
    isWomen () {
      return this.deal.reference === MembershipTypes.women
    },
    isSneakerSocks () {
      return this.deal.reference === MembershipTypes.sneakerSocks
    },
    isSocksRegular () {
      return this.deal.reference === MembershipTypes.socksRegular
    },
    isBoysOriginals () {
      return this.deal.reference === MembershipTypes.boysOriginals
    },
    isMenOriginals () {
      return this.deal.reference === MembershipTypes.menOriginals
    },
    isMenOne () {
      return this.deal.reference === MembershipTypes.menOne
    },
    isUndershirts () {
      return this.deal.reference === MembershipTypes.undershirts
    },
    productImageOne () {
      // Men Originals Boxershorts
      let path = '/images/added-member/deal/men_originals-deal'

      if (this.isWomen) {
        path = '/images/added-member/deal/women-deal'
      }

      if (this.isSneakerSocks) {
        path = '/images/added-member/deal/socks_sneaker-deal'
      }

      if (this.isSocksRegular) {
        path = '/images/added-member/deal/socks_regular-deal'
      }

      if (this.isBoysOriginals) {
        path = '/images/added-member/deal/boys-deal'
      }

      if (this.isMenOne) {
        path = '/images/added-member/deal/men_one-deal'
      }

      if (this.isUndershirts) {
        path = '/images/added-member/deal/undershirt-deal'
      }

      return getImagePaths({
        path,
        s3: true
      })
    },
    isFirstForFree () {
      return this.deal.campaignEligible && this.deal.campaignStrategy === CampaignStrategies.extraOrder
    },
    usps () {
      const usps = []

      // svg icons
      const gift = 'new-gift-outline'
      const socks = 'membership-socks'
      const coin = 'coin-outline'
      const truck = 'truck-blanc'
      const boxershort = 'membership-boxershort'
      const membershipWomen = 'membership-women'

      if (this.isUndershirts) {
        if (this.hasDiscount) {
          usps.push({
            icon: gift,
            text: this.getTranslation('USPS.UndershirtDiscount')
          })

          usps.push({
            icon: coin,
            text: this.getTranslation('USPS.CombiDeal')
          })
        }

        usps.push({
          icon: truck,
          text: this.getTranslation('USPS.MonthlyDeliveredForFree')
        })
      }

      if (this.isSneakerSocks || this.isSocksRegular) {
        if (!this.isFirstForFree && !this.hasDiscount) {
          usps.push({
            icon: gift,
            text: this.getTranslation('USPS.NewDesignEveryMonth')
          })
        }
        if (this.isFirstForFree) {
          usps.push({
            icon: gift,
            text: this.getTranslation('USPS.FirstPairOfSocksForFree')
          })
        }

        usps.push({
          icon: socks,
          text: this.getTranslation('USPS.SocksMatchWithMenOriginalsBoxershort')
        })

        if (this.hasDiscount) {
          usps.push({
            icon: coin,
            text: this.getTranslation('USPS.CombiDeal')
          })
        }

        usps.push({
          icon: truck,
          text: this.getTranslation('USPS.MonthlyDeliveredForFree')
        })
      }

      if (this.isBoysOriginals) {
        usps.push({
          icon: boxershort,
          text: this.getTranslation('USPS.BoxershortWithPerfectFit')
        })

        usps.push({
          icon: gift,
          text: this.getTranslation('USPS.PerfectMatchBoxershort')
        })

        usps.push({
          icon: truck,
          text: this.getTranslation('USPS.MonthlyDeliveredForFree')
        })
      }

      if (this.isMenOne) {
        usps.push({
          icon: gift,
          text: this.getTranslation('USPS.MenOneBoxershortWithPerfectQuality')
        })

        usps.push({
          icon: boxershort,
          text: this.getTranslation('USPS.BoxershortWithPerfectFit')
        })

        usps.push({
          icon: truck,
          text: this.getTranslation('USPS.MonthlyDeliveredForFree')
        })
      }

      if (this.isMenOriginals) {
        usps.push({
          icon: gift,
          text: this.getTranslation('USPS.NewDesignEveryMonth')
        })

        usps.push({
          icon: boxershort,
          text: this.getTranslation('USPS.BoxershortWithPerfectFit')
        })

        usps.push({
          icon: truck,
          text: this.getTranslation('USPS.MonthlyDeliveredForFree')
        })
      }

      if (this.isWomen) {
        usps.push({
          icon: gift,
          text: this.getTranslation('USPS.Every3MonthsFreeBralette')
        })

        usps.push({
          icon: membershipWomen,
          text: this.getTranslation('USPS.WomenAllItemsCanBeCombined')
        })

        usps.push({
          icon: truck,
          text: this.getTranslation('USPS.MonthlyDeliveredForFree')
        })
      }

      return usps
    }
  },
  methods: {
    onSelectDealClick (deal = {}) {
      this.$emit('select', { ...deal, hasDiscount: this.hasDiscount, campaignStrategy: this.deal.campaignStrategy, designSlug: this.latestDesignSlug })
    },
    getTranslation (key) {
      const discountPercentage = Math.round(((this.prices.originalPriceNumber - this.prices.discountPriceNumber) / this.prices.originalPriceNumber) * 100)

      return this.$t(`ExploreMemberships.${key}`, {
        ...this.prices,
        discountPercentage: discountPercentage === 44 ? 45 : discountPercentage
      })
    }
  }
}
</script>

<style scoped lang="postcss">
.deal-showcase {
  .text-price {
    @apply font-extrabold;
    font-size: 2.4rem;
    color: rgba(0, 163, 81, 1);
  }

  &-image-container {
    min-height: 22.9rem;
  }

  .font-14 {
    font-size: 1.4rem !important;
  }

  .font-24 {
    font-size: 2.4rem !important;
  }

  svg {
    @apply fill-neutral-600 !important;

    path {
      @apply fill-neutral-600 !important;
    }

    g {
      @apply fill-neutral-600 !important;
    }
  }

  .rounded-4rem {
    border-radius: 4rem;
  }
}

@screen md {
  .deal-showcase {
    &-image-container {
      border-top-left-radius: 1.6rem;
      border-top-right-radius: 1.6rem;
    }
  }
}

@screen lg {
  .deal-showcase {
    &-image-container {
      border-top-left-radius: 1.6rem;
      border-top-right-radius: 1.6rem;
    }
  }
}
</style>
